import { useEffect, useState } from "react";

// Parse a timestamp the API returns. SQLite's datetime('now') is a
// naive UTC string ("YYYY-MM-DD HH:MM:SS") — no T, no Z — which JS
// would otherwise interpret as the user's *local* time, making fresh
// rows look like they're in the future for anyone east of UTC. Treat
// any timezone-less SQL-style timestamp as UTC.
function parseServerTime(iso: string): number {
  if (!iso) return NaN;
  let s = iso;
  // SQLite "YYYY-MM-DD HH:MM:SS" → ISO-ish with a T separator.
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) s = s.replace(" ", "T");
  // If still no timezone marker, the server gave us UTC — say so.
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
  return new Date(s).getTime();
}

// Format an ISO timestamp as a short relative string ("just now", "12s
// ago", "3m ago", "5h ago", or the locale date). Same vocabulary the
// individual builders had inline — now centralized so RelativeTime can
// auto-refresh callers.
export function relativeTimeString(iso: string): string {
  const t = parseServerTime(iso);
  if (!Number.isFinite(t)) return "";
  let ms = Date.now() - t;
  // Allow a small skew (client clock drift) before tipping into a
  // "future" label. Within 2s either side we still say "just now".
  if (ms < -2_000) return "in the future";
  if (ms < 0) ms = 0;
  if (ms < 2_000) return "just now";
  if (ms < 60_000) return `${Math.floor(ms / 1_000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return new Date(t).toLocaleDateString();
}

// Re-render cadence. The label only steps through "just now / Xs ago /
// Ym ago / Zh ago / date" — there's no value in ticking every second
// once the row stops moving. 2-minute heartbeat is plenty for the
// fidelity we render, and keeps long lists cheap.
function refreshIntervalMs(_iso: string): number {
  return 120_000;
}

// Self-updating relative timestamp. Renders as a <time> element with the
// absolute time in the title so hover still reveals the precise moment.
export default function RelativeTime({
  iso,
  className = "",
  prefix = "",
}: { iso: string; className?: string; prefix?: string }) {
  const [, force] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const schedule = () => {
      const wait = refreshIntervalMs(iso);
      const id = window.setTimeout(() => {
        if (cancelled) return;
        force((n) => n + 1);
        schedule();
      }, wait);
      return id;
    };
    const id = schedule();
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [iso]);

  const t = parseServerTime(iso);
  const title = Number.isFinite(t) ? new Date(t).toLocaleString() : iso;
  return (
    <time dateTime={iso} title={title} className={className}>
      {prefix}{relativeTimeString(iso)}
    </time>
  );
}
